import { enumMembers, type ApiSchema } from "../apiSchema";
import { readArray, readBoolean, readInteger, readNullableString, readObject, readOneOf, readString } from "../http/jsonDecoding";
import { readStringList } from "./contentDecoders";

/**
 * What blocks a merge (reviews.md R-3), as the review page and the editor's check read it:
 * the server's verdict and the shapes violations of a head, or of a draft, split against
 * its base. One decoder for both.
 */

export type CheckFinding = Readonly<Pick<ApiSchema<"CheckFindingBody">, "message" | "focus" | "rule" | "severity">>;
export interface ShapeViolation {
  readonly checkId: string;
  readonly checkTitle: string | null;
  readonly finding: CheckFinding;
}
/** A head's shapes violations split against its base (R-3): the review's, and the editor's for a draft. */
export interface ShapeViolations {
  readonly introduced: readonly ShapeViolation[];
  readonly preExisting: readonly ShapeViolation[];
  /** New violations the engine's counts prove beyond the listed ones: they block too. */
  readonly unlistedIntroduced: number;
  /** Checks neither the lists nor the counts could compare, or whose base did not run: they block nothing (R-3). */
  readonly uncomparedCheckIds: readonly string[];
}
/** The server's R-3 verdict; the web never infers it from the listed findings. */
export interface MergeBlocks {
  readonly blocked: boolean;
  readonly inconsistencyCheckIds: readonly string[];
  readonly shapeViolations: ShapeViolations;
}

export const readFindingMessage = (value: unknown): string => readString(readObject(value, "finding"), "message");
function readShapeViolation(value: unknown): ShapeViolation {
  const violation = readObject(value, "shape violation");
  const finding = readObject(violation["finding"], "finding");
  return {
    checkId: readString(violation, "check_id"), checkTitle: readNullableString(violation, "check_title"),
    finding: {
      message: readFindingMessage(finding), focus: readNullableString(finding, "focus"), rule: readNullableString(finding, "rule"),
      severity: readOneOf(finding, "severity", enumMembers<ApiSchema<"FindingSeverity">>({ violation: true, warning: true, info: true })),
    },
  };
}
export function readShapeViolations(value: unknown): ShapeViolations {
  const shapes = readObject(value, "shape violations");
  return {
    introduced: readArray(shapes["introduced"], "introduced violations", readShapeViolation),
    preExisting: readArray(shapes["pre_existing"], "pre-existing violations", readShapeViolation),
    unlistedIntroduced: readInteger(shapes, "unlisted_introduced"),
    uncomparedCheckIds: readStringList(shapes, "uncompared_check_ids"),
  };
}
export function readMergeBlocks(value: unknown): MergeBlocks {
  const blocks = readObject(value, "merge blocks");
  return {
    blocked: readBoolean(blocks, "blocked"), inconsistencyCheckIds: readStringList(blocks, "inconsistency_check_ids"),
    shapeViolations: readShapeViolations(blocks["shape_violations"]),
  };
}
